/**
 * Раздел «Проекты» — новая редакция (US-26), на выдаче US-27 / DP-446.
 *
 * Состав экрана в фиксированном порядке (FR-1.2): плашка **шести**
 * показателей со сноской об охвате → реестр проектов → два виджета в ряд
 * («Освоение по заказчикам (АБП)» и «Крупнейшие проекты»). Карточка
 * проекта — модальное окно поверх (FR-4).
 *
 * Эта редакция **заменяет** плашку, реестр и карточку US-05.1, а виджеты
 * «Структура портфеля», «Классификация по отраслям», «Способы закупки» и
 * «проекты с риском» убраны с раздела совсем (FR-1.1, FR-1.4, решение 4).
 *
 * Данные — только с бэка, пятью раздельными операциями: плашка и виджеты
 * считаются по всей территории, реестр фильтруется и постранично
 * отдаётся, и плашку это не меняет (FR-2.6, FR-3.5). Запросы независимы:
 * медленный не задерживает отрисовку остальных (НФТ «Отзывчивость»).
 *
 * Подписи берутся из шапки ответа, а не додумываются экраном: год плиток
 * и колонок — `report_year` (FR-2.2), сноска об охвате — `coverage`
 * (FR-2.3), «последние данные» — `actual_at` (US-27 FR-2.2).
 *
 * Четыре состояния «пусто» различимы (FR-8.3): виджет выключен
 * супер-администратором (`widget_enabled` + реестр витрины) · уровень
 * КАТО раздел не обслуживает (`level_supported`, FR-1.5) · источник по
 * региону не подключён (`source_connected`) · по территории проектов нет
 * (`has_data`). Пятое — отсутствие отдельной величины — это `null`, и от
 * настоящего нуля оно отличается (FR-8.1, FR-8.2).
 */
import { Building2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AbpWidget } from './AbpWidget';
import { fmt, loadDate, moneyLarge, pct1 } from './format';
import { LargestProjectsWidget } from './LargestProjectsWidget';
import { ProjectCardDialog } from './ProjectCardDialog';
import { ProjectsRegistry } from './ProjectsRegistry';
import { useLocalizedText } from './projectsView';
import { BlockLoader } from '../../../components/common/BlockLoader';
import { StatCard } from '../../../components/widgets/StatCard';
import { MODULES } from '../../../consts/modules';
import { useKato } from '../../../contexts/KatoContext';
import { useYear } from '../../../contexts/YearContext';
import { useModulePresent } from '../../../hooks/useModulePresent';
import { katoLevel } from '../../../lib/kato-level';
import {
  getProjectCard,
  getProjectsAbp,
  getProjectsLargest,
  getProjectsRegistry,
  getProjectsSummary,
} from '../../../requests/projects.request';
import type {
  ProjectCard,
  ProjectsAbpData,
  ProjectsBlockHead,
  ProjectsLargestData,
  ProjectsRegistryData,
  ProjectsRegistryQuery,
  ProjectsSummary,
} from '../../../types/projects';

/**
 * Раздел применяется только на уровнях области, района и города
 * (FR-1.5, решение 5): территория проекта восстанавливается из БИНа
 * заказчика и глубже района не идёт. Уровень проверяется до запроса —
 * пустой ответ читался бы как «проектов нет», что неверно.
 */
const isSupportedLevel = (kato: number | null): boolean => {
  if (kato === null) return false;
  const level = katoLevel(kato);
  return level === 'OBLAST' || level === 'DISTRICT';
};

const EMPTY_QUERY: ProjectsRegistryQuery = {
  status: null,
  abpCode: null,
  projectType: null,
  search: '',
  page: 1,
};

interface Loaded<T> {
  data: T | null;
  loading: boolean;
}

/**
 * Один независимый запрос элемента раздела. Выключенный витриной или
 * неприменимый по уровню элемент не запрашивается вовсе; отказ запроса
 * деградирует в пустое состояние без ошибки интерфейса.
 */
const useProjectsRequest = <T,>(
  enabled: boolean,
  kato: number | null,
  year: number | null,
  load: (kato: number, year: number | null) => Promise<T>,
  label: string,
): Loaded<T> => {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled || kato === null) {
      setData(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    load(kato, year)
      .then((response) => {
        if (!cancelled) setData(response);
      })
      .catch((err) => {
        if (!cancelled) {
          console.error(`projects: ${label} request failed`, err);
          setData(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // `load` и `label` — стабильные модульные ссылки.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, kato, year]);

  return { data, loading };
};

/** Плитка плашки: показатель без данных на экран не выводится (FR-2.8). */
interface Tile {
  key: string;
  title: string;
  value: string;
  unit: string | null;
  footnote: string | null;
}

export const ProjectsSection = () => {
  const { t } = useTranslation();
  const loc = useLocalizedText();
  const { selectedKato } = useKato();
  const { selectedYear } = useYear();

  // FR-9.3: состав раздела задаёт супер-администратор; выключенный
  // элемент не рендерится и данных не запрашивает. Идентификаторы — из
  // каталога бэка (DP-446), их же операции возвращают в `module_id`.
  const showPlashka = useModulePresent(MODULES.projects.plashka);
  const showRegistry = useModulePresent(MODULES.projects.registry);
  const showAbp = useModulePresent(MODULES.projects.abp);
  const showLargest = useModulePresent(MODULES.projects.largest);

  const levelOk = isSupportedLevel(selectedKato);

  const summary = useProjectsRequest<ProjectsSummary>(
    levelOk && showPlashka,
    selectedKato,
    selectedYear,
    getProjectsSummary,
    'summary',
  );
  const abp = useProjectsRequest<ProjectsAbpData>(
    levelOk && showAbp,
    selectedKato,
    selectedYear,
    getProjectsAbp,
    'abp',
  );
  const largest = useProjectsRequest<ProjectsLargestData>(
    levelOk && showLargest,
    selectedKato,
    selectedYear,
    getProjectsLargest,
    'largest',
  );

  // --- Реестр: фильтры, поиск и страница уходят в запрос (US-27 FR-1.4) ---
  const [query, setQuery] = useState<ProjectsRegistryQuery>(EMPTY_QUERY);
  const [registry, setRegistry] = useState<ProjectsRegistryData | null>(null);
  const [registryLoading, setRegistryLoading] = useState(false);

  // FR-9.1: смена территории или года сбрасывает фильтры и страницу —
  // фильтра по чужому заказчику в новой выдаче может не быть вовсе.
  useEffect(() => {
    setQuery(EMPTY_QUERY);
  }, [selectedKato, selectedYear]);

  useEffect(() => {
    if (!levelOk || !showRegistry || selectedKato === null) {
      setRegistry(null);
      setRegistryLoading(false);
      return;
    }
    let cancelled = false;
    setRegistryLoading(true);
    getProjectsRegistry(selectedKato, selectedYear, query)
      .then((response) => {
        if (!cancelled) setRegistry(response);
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('projects: registry request failed', err);
          setRegistry(null);
        }
      })
      .finally(() => {
        if (!cancelled) setRegistryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [levelOk, showRegistry, selectedKato, selectedYear, query]);

  // --- Карточка проекта: своя операция по `project_uid` (US-27 FR-1.5) ---
  const [openedUid, setOpenedUid] = useState<string | null>(null);
  const [card, setCard] = useState<ProjectCard | null>(null);
  const [cardLoading, setCardLoading] = useState(false);

  // FR-9.1: смена территории закрывает открытую карточку.
  useEffect(() => {
    setOpenedUid(null);
    setCard(null);
  }, [selectedKato]);

  useEffect(() => {
    if (openedUid === null) return;
    let cancelled = false;
    setCardLoading(true);
    setCard(null);
    getProjectCard(openedUid)
      .then((response) => {
        if (!cancelled) setCard(response);
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('projects: card request failed', err);
          setCard(null);
        }
      })
      .finally(() => {
        if (!cancelled) setCardLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [openedUid]);

  const closeCard = useCallback(() => {
    setOpenedUid(null);
    setCard(null);
  }, []);

  const header = (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 py-3">
      <h1 className="shrink-0 text-xl font-extrabold">{t('projects.title')}</h1>
      <div className="h-px flex-1 bg-surface-border" aria-hidden="true" />
      <Building2
        className="size-6 shrink-0 text-brand-600"
        strokeWidth={1.8}
        aria-hidden="true"
      />
    </div>
  );

  // FR-1.5 / Сценарий 27: «уровень неприменим» — отдельное состояние,
  // отличимое от «нет данных»; пустые плашка и реестр не показываются.
  if (!levelOk) {
    return (
      <article className="space-y-3.5" data-testid="projects-section">
        {header}
        <p
          className="rounded-card border border-surface-border bg-surface p-5 text-sm text-text-muted"
          data-testid="projects-level-not-supported"
        >
          {t('projects.levelNotSupported')}
        </p>
      </article>
    );
  }

  // Шапка у всех операций общая — берём первую пришедшую: выключенный
  // витриной элемент не запрашивался и ответа не дал.
  const head: ProjectsBlockHead | null =
    summary.data ?? registry ?? abp.data ?? largest.data;

  if (head !== null && !head.levelSupported) {
    return (
      <article className="space-y-3.5" data-testid="projects-section">
        {header}
        <p
          className="rounded-card border border-surface-border bg-surface p-5 text-sm text-text-muted"
          data-testid="projects-level-not-supported"
        >
          {t('projects.levelNotSupported')}
        </p>
      </article>
    );
  }

  // US-27 FR-6.5 / Сценарий 17: регион без подключённого источника — не
  // регион без строек; пустые плашка и реестр тут врали бы.
  if (head !== null && !head.sourceConnected) {
    return (
      <article className="space-y-3.5" data-testid="projects-section">
        {header}
        <p
          className="rounded-card border border-surface-border bg-surface p-5 text-sm text-text-muted"
          data-testid="projects-source-not-connected"
        >
          {t('projects.sourceNotConnected')}
        </p>
      </article>
    );
  }

  const s = summary.data;
  const year = head?.reportYear ?? null;

  const money = (tenge: number): { value: string; unit: string } => {
    const parts = moneyLarge(tenge);
    return {
      value: parts.value,
      unit: t(parts.mlrd ? 'projects.units.mlrdTg' : 'projects.units.mlnTg'),
    };
  };

  // FR-2.1: шесть плиток в фиксированном порядке; плитка без данных в
  // ряд не попадает, остальные растягиваются по ширине (FR-2.8).
  const tiles: Tile[] = [];
  if (s !== null && s.hasData) {
    tiles.push({
      key: 'projects',
      title: t('projects.plashka.projects'),
      value: `${fmt(s.projectCount)} / ${fmt(s.contractCount)}`,
      unit: t('projects.units.pcs'),
      footnote: t('projects.plashka.completed', { value: s.completedCount }),
    });
    const m = s.money;
    if (m.totalCost !== null) {
      const v = money(m.totalCost);
      tiles.push({
        key: 'totalCost',
        title: t('projects.plashka.totalCost'),
        value: v.value,
        unit: v.unit,
        footnote: t('projects.plashka.totalCostSub'),
      });
    }
    if (m.spentBeforeYear !== null) {
      const v = money(m.spentBeforeYear);
      tiles.push({
        key: 'spentBefore',
        title: t('projects.plashka.spentBefore', { year: year ?? '' }),
        value: v.value,
        unit: v.unit,
        footnote: t('projects.plashka.spentBeforeSub', { year: year ?? '' }),
      });
    }
    if (m.planYear !== null) {
      const v = money(m.planYear);
      tiles.push({
        key: 'planYear',
        title: t('projects.plashka.planYear', { year: year ?? '' }),
        value: v.value,
        unit: v.unit,
        footnote: t('projects.plashka.planYearSub'),
      });
    }
    if (m.spentInYear !== null) {
      const v = money(m.spentInYear);
      tiles.push({
        key: 'spentInYear',
        title: t('projects.plashka.spentInYear', { year: year ?? '' }),
        value: v.value,
        unit: v.unit,
        // FR-2.5: процент к ГОДОВОМУ ПЛАНУ — не «освоение» карточки.
        footnote:
          m.annualPlanPct !== null
            ? t('projects.plashka.spentInYearSub', {
                pct: pct1(m.annualPlanPct),
              })
            : null,
      });
    }
    if (m.remainder !== null) {
      const v = money(m.remainder);
      tiles.push({
        key: 'remainder',
        title: t('projects.plashka.remainder'),
        value: v.value,
        unit: v.unit,
        footnote: t('projects.plashka.remainderSub'),
      });
    }
  }

  // FR-2.7: сноска об охвате строится из `coverage` выдачи, а не зашита в
  // интерфейс — правило отбора уже менялось дважды.
  const coverage = head?.coverage;
  const scopeNote =
    [loc(coverage?.included ?? null), loc(coverage?.excluded ?? null)]
      .filter(Boolean)
      .join(' — ') || t('projects.plashka.scope');

  const actualAt = loadDate(head?.actualAt ?? null);
  // US-27 FR-2.4: отдан не запрошенный год — это подписывается, а не
  // подменяется молча.
  const yearFallback =
    head !== null && !head.yearIsRequested && head.requestedYear !== null;

  return (
    <article className="space-y-3.5" data-testid="projects-section">
      {header}

      {(actualAt !== null || yearFallback) && (
        <p className="text-xs text-text-faint" data-testid="projects-actual-at">
          {actualAt !== null && t('projects.actualAt', { date: actualAt })}
          {actualAt !== null && yearFallback && ' · '}
          {yearFallback &&
            t('projects.yearFallback', {
              requested: head?.requestedYear,
              actual: year ?? '',
            })}
        </p>
      )}

      {showPlashka &&
        (summary.loading ? (
          <BlockLoader testId="projects-plashka-loader" />
        ) : (
          tiles.length > 0 && (
            <div data-testid="block-projects-plashka">
              <div className="flex flex-wrap gap-3.5">
                {tiles.map((tile) => (
                  <div key={tile.key} className="min-w-52 flex-1">
                    <StatCard
                      testId={`projects-card-${tile.key}`}
                      title={tile.title}
                      value={tile.value}
                      unit={tile.unit}
                      footnotes={
                        tile.footnote !== null ? [{ text: tile.footnote }] : []
                      }
                    />
                  </div>
                ))}
              </div>
              <p
                className="mt-2 text-xs text-text-faint"
                data-testid="projects-plashka-scope"
              >
                {scopeNote}
              </p>
            </div>
          )
        ))}

      {showRegistry && (
        <ProjectsRegistry
          data={registry}
          loading={registryLoading}
          query={query}
          onQueryChange={setQuery}
          abpOptions={abp.data?.items ?? []}
          onOpen={setOpenedUid}
        />
      )}

      <div className="grid grid-cols-1 gap-3.5 xl:grid-cols-2">
        {showAbp &&
          (abp.loading ? (
            <BlockLoader testId="projects-abp-loader" />
          ) : (
            <AbpWidget rows={abp.data?.items ?? []} />
          ))}
        {showLargest &&
          (largest.loading ? (
            <BlockLoader testId="projects-largest-loader" />
          ) : (
            <LargestProjectsWidget
              rows={largest.data?.items ?? []}
              onOpen={setOpenedUid}
            />
          ))}
      </div>

      <ProjectCardDialog
        card={card}
        open={openedUid !== null}
        loading={cardLoading}
        onClose={closeCard}
      />
    </article>
  );
};
